import {
	locationOptionAddressDetailSX,
	locationOptionAliasSX,
	locationOptionFavoriteAddressSX,
	locationOptionIconContainerSX,
} from '@/components/content/Header/parts/DeliveryLocator/styles/LocationOption/locationOptionStyles';
import {
	selectedLocationContainerSX,
	selectedLocationLocalityTextSX,
	selectedLocationTagContainerSX,
	selectedLocationTagSX,
	selectedLocationTagTextSX,
} from '@/components/content/Header/parts/DeliveryLocator/styles/SelectedLocationBox/selectedLocationBoxStyles';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useLocalization } from '@/data/Localization';
import { Address } from '@/data/types/Address';
import { Box, Stack, Typography } from '@mui/material';
import {
	MATERIAL_SYMBOL_ICONS,
	OpenSansFontWeights,
} from 'shared/custom/constants/commonConstants';
import { formatExteriorNumberWithHash } from 'shared/custom/utils/exteriorNumber';
import { formatLocality } from 'shared/custom/utils/formatLocality';

type Props = {
	selectedAddress?: Address;
	locality: string;
	state: string;
	zipCode: string;
	userEmail: string;
};

export const SelectedLocationBox = ({
	selectedAddress,
	locality,
	state,
	zipCode,
	userEmail,
}: Props) => {
	const localization = useLocalization('DeliveryLocator');
	const addressLabel = useLocalization('AddressCard');
	const { addressLine1, addressLine2, addressLine3, city, nickName, primary } =
		selectedAddress ?? {};
	const isFavoriteAddress = primary && JSON.parse(primary);
	const exteriorNumber = formatExteriorNumberWithHash(addressLine2);
	const addressDetail = selectedAddress
		? `${addressLine1} ${exteriorNumber}, Col. ${addressLine3}, ${city}, C.P. ${zipCode}`
		: '';

	const addressNickNameFormatted =
		nickName?.toLowerCase() === userEmail.toLowerCase()
			? addressLabel.AccountAddress.t()
			: nickName;

	return (
		<Stack
			id="selectedLocation"
			className="delivery-location__selected"
			sx={selectedLocationContainerSX}
		>
			<Box sx={selectedLocationTagContainerSX}>
				<Box sx={selectedLocationTagSX}>
					<Typography variant="bodySubText" fontWeight={OpenSansFontWeights.semibold}>
						{localization.locationLegend.t()}
					</Typography>
				</Box>
				<Typography
					variant="bodySubText"
					fontWeight={OpenSansFontWeights.semibold}
					sx={selectedLocationTagTextSX}
				>
					{localization.zipCode.t()}
				</Typography>
				<Typography
					className="delivery-location__zipcode"
					variant="bodySubText"
					fontWeight={OpenSansFontWeights.semibold}
					sx={selectedLocationTagTextSX}
				>
					{zipCode}
				</Typography>
			</Box>
			{selectedAddress ? (
				<>
					<Typography variant="bodyBaseline" sx={locationOptionAliasSX}>
						{addressNickNameFormatted}
					</Typography>
					{isFavoriteAddress ? (
						<Typography variant="bodySubText" sx={locationOptionFavoriteAddressSX}>
							{localization.Labels.favoriteAddress.t()}
						</Typography>
					) : null}
					<Box sx={locationOptionAddressDetailSX}>
						<DynamicIcon
							name={MATERIAL_SYMBOL_ICONS.locationOn}
							variant="filled"
							sx={locationOptionIconContainerSX}
						/>
						<Typography variant="bodySubText" className="delivery-location__details">
							{addressDetail.toLowerCase()}
						</Typography>
					</Box>
				</>
			) : (
				<Typography
					className="delivery-location__locality"
					variant="bodySubText"
					fontWeight={OpenSansFontWeights.regular}
					sx={selectedLocationLocalityTextSX}
				>
					{formatLocality(locality ? `${locality}, ${state}` : state)}
				</Typography>
			)}
		</Stack>
	);
};
